import { isNativeApp, nativePlatform } from './native';

/**
 * What the browser does by itself and a phone webview does not. Installed once
 * at startup; a NO-OP in a browser, so the cashier is untouched.
 *
 *   reloadFromServer()  the «تحديث» button: throws away the running page and
 *                       fetches everything again from pos.matix.one.
 *   back button         ⚠ ANDROID: see BACK_EVENT below. The till has no router,
 *                       so `location.pathname` never changes — copying the ERP
 *                       shell's `homePaths` rule here would exit the app from
 *                       EVERY screen, cart and all.
 *   status bar          follows the light/dark theme.
 *   pinch-zoom          off inside the app (the till layout is fixed).
 */

/**
 * Fired on the Android back button. The cashier may `preventDefault()` to
 * handle it itself (close a modal, step back in the cart). If nobody does, the
 * shell asks for a SECOND press within BACK_CONFIRM_MS before it exits — a
 * single stray press must never discard a half-rung sale.
 */
export const BACK_EVENT = 'matix:back';
export const BACK_CONFIRM_MS = 2500;
/** Fired when the shell wants the app to show "press back again to exit". */
export const BACK_CONFIRM_EVENT = 'matix:back-confirm';

let installed = false;

/**
 * Reload the cashier from the server, bypassing the page that is running now.
 *
 * In the app this is the «تحديث» button: the shell holds no copy of the
 * cashier, so a reload fetches the current deploy from pos.matix.one. In a
 * browser it is the ordinary reload.
 */
export function reloadFromServer(): void {
  if (typeof window === 'undefined') return;
  // `location.reload()` is enough — the document's URL IS the server URL, and
  // the webview revalidates against it. `replace` would also drop the history
  // entry, which the back button needs.
  window.location.reload();
}

/** Hide the native splash once the app has painted. A no-op in a browser. */
export function hideNativeSplash(): void {
  if (!isNativeApp()) return;
  requestAnimationFrame(() => {
    void import('@capacitor/splash-screen')
      .then(({ SplashScreen }) => SplashScreen.hide())
      .catch(() => {});
  });
}

/** rgb(a)() → #rrggbb, for StatusBar.setBackgroundColor. */
function toHex(rgb: string): string {
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(rgb);
  if (!m) return '#FFFFFF';
  return `#${[m[1], m[2], m[3]].map((x) => Number(x).toString(16).padStart(2, '0')).join('')}`;
}

/** Matix hosts stay in the app; anything else goes to the system browser. */
export const MATIX_HOSTS = /(^|\.)matix\.one$/i;

type FilePlugins = {
  Filesystem: typeof import('@capacitor/filesystem').Filesystem;
  Directory: typeof import('@capacitor/filesystem').Directory;
  Share: typeof import('@capacitor/share').Share;
  Browser: typeof import('@capacitor/browser').Browser;
};

let filePluginsPromise: Promise<FilePlugins> | null = null;
function filePlugins(): Promise<FilePlugins> {
  filePluginsPromise ??= Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
    import('@capacitor/browser'),
  ]).then(([fs, share, browser]) => ({
    Filesystem: fs.Filesystem,
    Directory: fs.Directory,
    Share: share.Share,
    Browser: browser.Browser,
  }));
  return filePluginsPromise;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((ok, no) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result).split(',', 2)[1] ?? '');
    r.onerror = () => no(r.error);
    r.readAsDataURL(blob);
  });
}

function safeName(name: string): string {
  const n = name.replace(/[\\/:*?"<>|]+/g, '_').trim();
  return n || `matix-pos-${Date.now()}`;
}

/**
 * Save a file the user asked for. In the app: written to the cache and handed
 * to the share sheet. In a browser: the ordinary download.
 *
 * ⚠ This is why the till's Excel export works on iOS. A WKWebView ignores the
 * `download` attribute, so `reportExcel.ts` — an <a download href="blob:…"> —
 * silently did nothing there.
 */
export async function saveFile(blob: Blob, filename: string): Promise<void> {
  if (!isNativeApp()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    return;
  }
  const { Filesystem, Directory, Share } = await filePlugins();
  const path = safeName(filename);
  const written = await Filesystem.writeFile({
    path,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
  });
  await Share.share({ title: path, files: [written.uri] });
}

export async function openExternal(url: string): Promise<void> {
  if (!isNativeApp()) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  const { Browser } = await filePlugins();
  await Browser.open({ url });
}

function leavesMatix(url: URL): boolean {
  return /^https?:$/.test(url.protocol) && url.origin !== location.origin && !MATIX_HOSTS.test(url.hostname);
}

/** An anchor click the webview would drop: a blob download, or a link off-site. */
function handleAnchor(a: HTMLAnchorElement): boolean {
  const href = a.getAttribute('href') ?? '';
  if (a.hasAttribute('download') && (href.startsWith('blob:') || href.startsWith('data:'))) {
    void fetch(href)
      .then((r) => r.blob())
      .then((b) => saveFile(b, a.getAttribute('download') || 'file'));
    return true;
  }
  try {
    if (leavesMatix(new URL(href, location.href))) {
      void openExternal(new URL(href, location.href).href);
      return true;
    }
  } catch {
    /* not a URL */
  }
  return false;
}

/**
 * ⚠ DELIBERATELY NOT PORTED from the ERP shell: its `window.open('')` capture
 * and `window.print()` override, which turn a print into a shared PDF.
 *
 * The till prints the customer receipt through `window.open('', '_blank')` +
 * `win.print()` (packages/receipt/src/printReceipt.ts). Hijacking that here
 * would replace every receipt with a "save this PDF" sheet — the opposite of
 * what a counter needs. Receipt printing is decided separately: pos-api already
 * sends the customer receipt to the thermal printer through SCPS
 * (`dispatchPrinting({ customer: true })`), and that path does not involve the
 * phone at all.
 */

export function installNativeBridge(): void {
  if (installed || !isNativeApp() || typeof window === 'undefined') return;
  installed = true;

  document.documentElement.dataset.nativeApp = nativePlatform();

  // Downloads and off-site links, caught at click. Capture phase for attached
  // anchors; the prototype patches catch the detached ones a download helper
  // builds and clicks without ever putting in the DOM.
  document.addEventListener('click', (e) => {
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (a && handleAnchor(a)) e.preventDefault();
  }, true);
  const nativeClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function patchedClick(this: HTMLAnchorElement) {
    if (!this.isConnected && handleAnchor(this)) return;
    nativeClick.call(this);
  };

  // No pinch-zoom inside the app — the till layout is built for the screen.
  const meta = document.querySelector('meta[name="viewport"]');
  const content = meta?.getAttribute('content') ?? '';
  if (meta && !/user-scalable/.test(content)) {
    meta.setAttribute('content', `${content}, maximum-scale=1, user-scalable=no`);
  }

  // ── Android back button ──────────────────────────────────────────────────
  // ⚠ The till has no router: every screen is `/`. So "am I on a home path?"
  // — the rule the ERP shell uses — is ALWAYS true here and would exit the app
  // from any screen. Instead: offer the page the event, then require a second
  // press. Nothing is discarded by one stray tap.
  let armedUntil = 0;
  void import('@capacitor/app').then(({ App }) => {
    void App.addListener('backButton', ({ canGoBack }) => {
      const handled = !window.dispatchEvent(new CustomEvent(BACK_EVENT, { cancelable: true }));
      if (handled) return;
      if (canGoBack) {
        history.back();
        return;
      }
      const now = Date.now();
      if (now < armedUntil) {
        void App.exitApp();
        return;
      }
      armedUntil = now + BACK_CONFIRM_MS;
      window.dispatchEvent(new CustomEvent(BACK_CONFIRM_EVENT));
    });
  });

  // ── Status bar follows the theme ─────────────────────────────────────────
  void import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    const apply = () => {
      const dark =
        document.documentElement.classList.contains('dark') ||
        document.documentElement.dataset.theme === 'dark';
      void StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
      if (nativePlatform() === 'android') {
        void StatusBar.setBackgroundColor({
          color: toHex(getComputedStyle(document.body).backgroundColor),
        }).catch(() => {});
      }
    };
    apply();
    new MutationObserver(apply).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
  });
}
